import {
  type Campaign,
  type CampaignCreateRequest,
  type CampaignDetail,
  type CampaignSegment,
  type CampaignSegmentPreview,
  type CampaignUpdateRequest,
  type CampaignsListQuerySchema,
  type NotificationDeliveriesListQuerySchema,
  type NotificationDelivery,
  type NotificationTemplate,
  type NotificationTemplateCreateRequest,
  type NotificationTemplateDetail,
  type NotificationTemplateUpdateRequest,
  type NotificationTemplatesListQuerySchema,
  type Paginated,
} from '@aysar/contracts';
import { createApi } from '@reduxjs/toolkit/query/react';
import type { z } from 'zod';

import { EVENT_BUS } from '@/lib/event-bus';
import { baseQueryWithAuth } from '@/shared/store/middleware/base-query-with-auth/base-query-with-auth';

/** The contracts' own INPUT shapes (U3) — never a hand-written copy of the filters. */
type TemplatesParams = z.input<typeof NotificationTemplatesListQuerySchema>;
type DeliveriesParams = z.input<typeof NotificationDeliveriesListQuerySchema>;
type CampaignsParams = z.input<typeof CampaignsListQuerySchema>;

/**
 * The copy the platform sends, and the record of what happened when it tried.
 *
 * ## One api for both, because the reactive edge crosses them
 *
 * A delivery's `template_missing` failure is fixed by **writing a template**,
 * and the operator does the two things in one sitting: author the copy, then go
 * back and retry the dead letters. One api's `invalidatesTags` cannot reach
 * another's cache (P17c), and splitting these would make the module's most
 * ordinary workflow an event-bus round trip to work around a boundary nothing
 * needed. `violationsApi` carries the queue and its rule catalogue for the same
 * reason.
 *
 * ## The catalogue is deliberately NOT here
 *
 * `GET /admin/notification-templates/catalogue` is read through the shared
 * bridge (`shared/services/notification-template-lookup`), because the
 * Violations rule form needs the same list and W6 forbids it importing this
 * file. Two caches of one resource is the drift W5/W6 exist to prevent — the
 * argument `settingsApi` records when it moved to `shared/` — so this module
 * reads the bridge like everybody else and announces its writes on the bus
 * instead (`notifications.template.changed`), which is what refreshes the
 * catalogue's `authoredChannels` everywhere at once.
 *
 * ## A retry returns the row, and the screen still re-reads
 *
 * `POST /:id/retry` answers with the re-queued delivery, and writing that
 * straight into the cache would save a round trip. Deliberately not done, for
 * the reason `violationsApi`, `moderationApi` and `trustApi` all give: rendering
 * from a mutation response gives the screen two sources for one row, and they
 * disagree the moment a colleague acts a second later — which on a **polling**
 * dead-letter view is the ordinary case rather than the unlucky one.
 */
export const notificationsApi = createApi({
  reducerPath: 'notificationsApi',
  baseQuery: baseQueryWithAuth,
  tagTypes: ['Templates', 'Template', 'Deliveries', 'Delivery', 'Campaigns', 'Campaign'],
  endpoints: (builder) => ({
    /**
     * The template list.
     *
     * `NotificationTemplatesService.list` goes through
     * `paginateFilteredLocalized`, which honours `sort`/`order` — so unlike the
     * violations queue this list has real sortable headers (P19b), and the
     * columns carry `sortKey`s.
     */
    listTemplates: builder.query<Paginated<NotificationTemplate>, TemplatesParams>({
      query: (params) => ({ url: '/admin/notification-templates', method: 'GET', params }),
      providesTags: ['Templates'],
    }),

    /** One template with EVERY locale — what the ar/en tabs prefill from (W7). */
    template: builder.query<NotificationTemplateDetail, string>({
      query: (id) => ({ url: `/admin/notification-templates/${id}`, method: 'GET' }),
      providesTags: (_result, _error, id) => [{ type: 'Template' as const, id }],
    }),

    createTemplate: builder.mutation<NotificationTemplateDetail, NotificationTemplateCreateRequest>(
      {
        query: (body) => ({ url: '/admin/notification-templates', method: 'POST', body }),
        invalidatesTags: ['Templates'],
        async onQueryStarted(_args, { queryFulfilled }) {
          await announceTemplateChange(queryFulfilled);
        },
      },
    ),

    updateTemplate: builder.mutation<
      NotificationTemplateDetail,
      { id: string; body: NotificationTemplateUpdateRequest }
    >({
      query: ({ id, body }) => ({
        url: `/admin/notification-templates/${id}`,
        method: 'PATCH',
        body,
      }),
      invalidatesTags: (_result, _error, { id }) => [
        { type: 'Template' as const, id },
        'Templates' as const,
      ],
      async onQueryStarted(_args, { queryFulfilled }) {
        await announceTemplateChange(queryFulfilled);
      },
    }),

    listDeliveries: builder.query<Paginated<NotificationDelivery>, DeliveriesParams>({
      query: (params) => ({ url: '/admin/notification-deliveries', method: 'GET', params }),
      providesTags: ['Deliveries'],
    }),

    /**
     * One delivery — the copy that was actually sent, and the vendor's own words.
     *
     * A separate request rather than the row the log already holds: the detail
     * dialog must not be reading a row the poll has since moved on from, which
     * is the same argument the violations review dialog makes.
     */
    delivery: builder.query<NotificationDelivery, string>({
      query: (id) => ({ url: `/admin/notification-deliveries/${id}`, method: 'GET' }),
      providesTags: (_result, _error, id) => [{ type: 'Delivery' as const, id }],
    }),

    retryDelivery: builder.mutation<NotificationDelivery, string>({
      query: (id) => ({ url: `/admin/notification-deliveries/${id}/retry`, method: 'POST' }),
      invalidatesTags: (_result, _error, id) => [
        { type: 'Delivery' as const, id },
        'Deliveries' as const,
      ],
    }),

    listCampaigns: builder.query<Paginated<Campaign>, CampaignsParams>({
      query: (params) => ({ url: '/admin/campaigns', method: 'GET', params }),
      providesTags: ['Campaigns'],
    }),

    /** One campaign with every locale and how its walk is going (W7). */
    campaign: builder.query<CampaignDetail, string>({
      query: (id) => ({ url: `/admin/campaigns/${id}`, method: 'GET' }),
      providesTags: (_result, _error, id) => [{ type: 'Campaign' as const, id }],
    }),

    /**
     * Who a segment reaches, right now.
     *
     * A **mutation** rather than a query, and not because it writes: it is a
     * POST (the segment is a nested object, not a query string), and RTK Query
     * caches queries by argument — a preview cached against a segment the
     * operator has since edited is the one answer on this screen that must never
     * be stale, because it is what the send is confirmed against.
     */
    previewSegment: builder.mutation<CampaignSegmentPreview, CampaignSegment>({
      query: (segment) => ({ url: '/admin/campaigns/preview', method: 'POST', body: { segment } }),
    }),

    createCampaign: builder.mutation<CampaignDetail, CampaignCreateRequest>({
      query: (body) => ({ url: '/admin/campaigns', method: 'POST', body }),
      invalidatesTags: ['Campaigns'],
      async onQueryStarted(_args, { queryFulfilled }) {
        await announceCampaignChange(queryFulfilled);
      },
    }),

    updateCampaign: builder.mutation<CampaignDetail, { id: string; body: CampaignUpdateRequest }>({
      query: ({ id, body }) => ({ url: `/admin/campaigns/${id}`, method: 'PATCH', body }),
      invalidatesTags: (_result, _error, { id }) => [
        { type: 'Campaign' as const, id },
        'Campaigns' as const,
      ],
      async onQueryStarted(_args, { queryFulfilled }) {
        await announceCampaignChange(queryFulfilled);
      },
    }),

    /**
     * The irreversible one.
     *
     * It invalidates `Deliveries` as well as the campaign: the walk starts
     * writing delivery rows immediately, and an operator who sends and then
     * opens the log must not be shown the page from before they pressed it.
     */
    sendCampaign: builder.mutation<CampaignDetail, string>({
      query: (id) => ({ url: `/admin/campaigns/${id}/send`, method: 'POST' }),
      invalidatesTags: (_result, _error, id) => [
        { type: 'Campaign' as const, id },
        'Campaigns' as const,
        'Deliveries' as const,
      ],
      async onQueryStarted(_args, { queryFulfilled }) {
        await announceCampaignChange(queryFulfilled);
      },
    }),

    cancelCampaign: builder.mutation<CampaignDetail, string>({
      query: (id) => ({ url: `/admin/campaigns/${id}/cancel`, method: 'POST' }),
      invalidatesTags: (_result, _error, id) => [
        { type: 'Campaign' as const, id },
        'Campaigns' as const,
      ],
      async onQueryStarted(_args, { queryFulfilled }) {
        await announceCampaignChange(queryFulfilled);
      },
    }),
  }),
});

/** Says a campaign moved, so any subscribed surface re-reads (W5). */
async function announceCampaignChange(
  queryFulfilled: Promise<{ data: CampaignDetail }>,
): Promise<void> {
  try {
    const { data } = await queryFulfilled;

    EVENT_BUS.publish({ type: 'notifications.campaign.changed', campaignId: data.id });
  } catch {
    // Refused — nothing moved, so there is nothing to announce. Swallowed for
    // the reason `announceTemplateChange` gives.
  }
}

/**
 * Says that the authored copy changed, so the shared catalogue bridge re-reads.
 *
 * `authoredChannels` is the half of a catalogue entry that is server state, and
 * it moves on exactly these two writes — the "nobody has written this yet" list
 * is wrong until it does.
 */
async function announceTemplateChange(
  queryFulfilled: Promise<{ data: NotificationTemplateDetail }>,
): Promise<void> {
  try {
    const { data } = await queryFulfilled;

    EVENT_BUS.publish({ type: 'notifications.template.changed', key: data.key });
  } catch {
    // Refused — nothing happened, so there is nothing to announce. Swallowed
    // deliberately: `onQueryStarted` has no caller, so a rejection escaping it
    // becomes an unhandled rejection. The failure is already the caller's,
    // through `unwrap()`.
  }
}

export const {
  useListTemplatesQuery,
  useTemplateQuery,
  useCreateTemplateMutation,
  useUpdateTemplateMutation,
  useListDeliveriesQuery,
  useDeliveryQuery,
  useRetryDeliveryMutation,
  useListCampaignsQuery,
  useCampaignQuery,
  usePreviewSegmentMutation,
  useCreateCampaignMutation,
  useUpdateCampaignMutation,
  useSendCampaignMutation,
  useCancelCampaignMutation,
} = notificationsApi;
